import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useGetMeUserQuery, useGetSponsorDetailsQuery } from '../../store/slices/apiSlice';
import { selectIsGetMeInitialized, selectIsSystemAdmin } from '../../store/slices/authSlice';
import { Box, Typography, LinearProgress, Fade } from '@mui/material';
import AnimatedBloqcubeLogo from '../Logo/AnimatedBloqcubeLogo';

// Configuration for loading screen behavior
const LOADING_CONFIG = {
  SHOW_DELAY: 300,        // Wait 300ms before showing loading (prevents flicker for fast responses)
  MIN_DISPLAY_TIME: 1000, // Show loading for at least 1 second once visible
  FADE_DURATION: 400,     // Fade transition duration in ms
};

/**
 * AuthProvider component that handles initial getMe and sponsor details API calls
 * After first load, uses persisted data and updates in background
 * Subsequent calls don't block the UI - uses persisted value immediately
 * 
 * Includes anti-flicker logic:
 * - Delays showing loading screen for fast responses
 * - Ensures minimum display time once shown
 * - Smooth fade transitions
 */
const AuthProvider = ({ children }) => {
  const isInitialized = useSelector(selectIsGetMeInitialized);
  const token = useSelector((state) => state.auth.token);
  const isSystemAdmin = useSelector(selectIsSystemAdmin);
  const { userData } = useSelector((state) => state.auth);

  // Anti-flicker state management
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const loadingStartTime = useRef(null);
  const showDelayTimer = useRef(null);
  const minDisplayTimer = useRef(null);

  // Only fetch getMe if we have a token
  const { 
    isLoading: isGetMeLoading, 
    isFetching: isGetMeFetching,
    data: getMeData,
    isUninitialized: isGetMeUninitialized,
    isError: isGetMeError,
  } = useGetMeUserQuery(undefined, {
    skip: !token || isSystemAdmin,
    // On first load, wait for response. After that, use cached/persisted data
    refetchOnMountOrArgChange: !isInitialized,
  });

  const sessionExpiredDialog = useSelector((state) => state.application.sessionExpiredDialog);

  // Get sponsorId from getMe data or fallback to token
  const sponsorId = useMemo(() => {
    return getMeData?.sponsorId || userData?.sponsorId || null;
  }, [getMeData, userData]);

  // Fetch sponsor details once we have sponsorId
  const { 
    isLoading: isSponsorLoading, 
    isFetching: isSponsorFetching,
    data: sponsorData,
    isUninitialized: isSponsorUninitialized,
  } = useGetSponsorDetailsQuery(sponsorId, {
    skip: !sponsorId || isSystemAdmin,
  });

  // Determine if getMe is actively loading or waiting to load
  const isGetMeInProgress = useMemo(() => {
    // Skip check for system admin
    if (isSystemAdmin || !token) return false;

    // Session expired or getMe failed — don't block the UI indefinitely
    if (sessionExpiredDialog || (isGetMeError && !isGetMeFetching)) return false;
    
    // Query is loading or fetching
    if (isGetMeLoading || isGetMeFetching) return true;
    
    // Query hasn't started yet but should (waiting for query to initialize)
    if (isGetMeUninitialized && !isInitialized) return true;
    
    // Token exists but no data yet and not initialized (just logged in)
    if (!isInitialized && !userData && !getMeData) return true;
    
    return false;
  }, [isSystemAdmin, token, isGetMeLoading, isGetMeFetching, isGetMeUninitialized, isInitialized, userData, getMeData, isGetMeError, sessionExpiredDialog]);

  // Determine if sponsor details is actively loading or waiting to load
  const isSponsorInProgress = useMemo(() => {
    // Skip if no sponsorId or system admin
    if (!sponsorId || isSystemAdmin) return false;
    
    // Query is loading or fetching
    if (isSponsorLoading || isSponsorFetching) return true;
    
    // Query hasn't started yet but should
    if (isSponsorUninitialized) return true;
    
    return false;
  }, [sponsorId, isSystemAdmin, isSponsorLoading, isSponsorFetching, isSponsorUninitialized]);

  // Calculate loading progress for visual feedback
  const loadingProgress = useMemo(() => {
    if (!token || isSystemAdmin) return 100;
    
    let progress = 0;
    if (!isGetMeInProgress && (getMeData || userData)) progress += 50;
    if (!isSponsorInProgress || !sponsorId) progress += 50;
    
    return progress;
  }, [isGetMeInProgress, isSponsorInProgress, getMeData, userData, sponsorId, token, isSystemAdmin]);

  // Get loading message based on current state
  const loadingMessage = useMemo(() => {
    if (isGetMeInProgress) return 'Loading your profile...';
    if (isSponsorInProgress) return 'Loading organization details...';
    return 'Almost ready...';
  }, [isGetMeInProgress, isSponsorInProgress]);

  // Determine if data is actually loading (raw state without anti-flicker)
  const isActuallyLoading = useMemo(() => {
    // No loading needed if no token or system admin
    if (!token || isSystemAdmin) return false;

    // Session expired or getMe failed — allow session dialog / login redirect
    if (sessionExpiredDialog || (isGetMeError && !isGetMeFetching)) return false;
    
    // If already initialized with cached data, don't block
    if (isInitialized && userData) return false;
    
    // Loading while getMe is in progress
    if (isGetMeInProgress) return true;
    
    // Loading while sponsor details are in progress (on first load only)
    if (!isInitialized && isSponsorInProgress) return true;
    
    return false;
  }, [token, isSystemAdmin, isInitialized, userData, isGetMeInProgress, isSponsorInProgress, sessionExpiredDialog, isGetMeError, isGetMeFetching]);

  // Anti-flicker effect: manage showing/hiding of loading screen
  useEffect(() => {
    // Cleanup function
    const cleanup = () => {
      if (showDelayTimer.current) {
        clearTimeout(showDelayTimer.current);
        showDelayTimer.current = null;
      }
      if (minDisplayTimer.current) {
        clearTimeout(minDisplayTimer.current);
        minDisplayTimer.current = null;
      }
    };

    if (isActuallyLoading) {
      // Data is loading - consider showing loading screen after delay
      if (!showLoadingScreen && !showDelayTimer.current) {
        showDelayTimer.current = setTimeout(() => {
          setShowLoadingScreen(true);
          setIsExiting(false);
          loadingStartTime.current = Date.now();
          showDelayTimer.current = null;
        }, LOADING_CONFIG.SHOW_DELAY);
      }
    } else {
      // Data finished loading
      cleanup();

      if (showLoadingScreen && !isExiting) {
        // Calculate how long loading screen has been shown
        const elapsedTime = loadingStartTime.current 
          ? Date.now() - loadingStartTime.current 
          : LOADING_CONFIG.MIN_DISPLAY_TIME;
        
        const remainingTime = Math.max(0, LOADING_CONFIG.MIN_DISPLAY_TIME - elapsedTime);

        if (remainingTime > 0) {
          // Wait for minimum display time before hiding
          minDisplayTimer.current = setTimeout(() => {
            setIsExiting(true);
            // Wait for fade out animation before fully hiding
            setTimeout(() => {
              setShowLoadingScreen(false);
              setIsExiting(false);
              loadingStartTime.current = null;
            }, LOADING_CONFIG.FADE_DURATION);
          }, remainingTime);
        } else {
          // Minimum time elapsed, start exit animation
          setIsExiting(true);
          setTimeout(() => {
            setShowLoadingScreen(false);
            setIsExiting(false);
            loadingStartTime.current = null;
          }, LOADING_CONFIG.FADE_DURATION);
        }
      }
    }

    return cleanup;
  }, [isActuallyLoading, showLoadingScreen, isExiting]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (showDelayTimer.current) clearTimeout(showDelayTimer.current);
      if (minDisplayTimer.current) clearTimeout(minDisplayTimer.current);
    };
  }, []);

  // Show loading screen with smooth transitions
  if (showLoadingScreen) {
    return (
      <Fade in={!isExiting} timeout={LOADING_CONFIG.FADE_DURATION}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            gap: 3,
            backgroundColor: '#FAFAFA',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
          }}
        >
          <Box sx={{ height: 120, width: 120 }}>
            <AnimatedBloqcubeLogo />
          </Box>
          <Typography 
            variant="h6" 
            color="text.secondary" 
            sx={{ 
              fontWeight: 500,
              transition: 'opacity 0.3s ease',
            }}
          >
            {loadingMessage}
          </Typography>
          <Box sx={{ width: 280 }}>
            <LinearProgress 
              variant="determinate" 
              value={loadingProgress} 
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: '#E0E0E0',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 3,
                  backgroundColor: '#1976d2',
                  transition: 'transform 0.4s ease',
                },
              }}
            />
          </Box>
          <Typography variant="caption" color="text.disabled">
            Please wait while we set things up for you
          </Typography>
        </Box>
      </Fade>
    );
  }

  // After initialization or if no token, render children immediately
  // Subsequent calls will update in background without blocking
  return <>{children}</>;
};

export default AuthProvider;

